import { isListItem } from 'common/Prose/utils/isListItem'
import { nodes, ExtractStrongNodeSpecAttributes } from 'prose-core'
import { ResolvedPos } from 'prosemirror-model'
import { Direction } from 'utils/coordUtils'

/**
 * Determines if you are attempting to drop a list_item inside a list
 * @param direction the direction of the drop
 * @param $draggedPos the resolved position of the dragged node
 * @param $dropTargetPos the resolved position of the drop target
 * @returns true if you're dropping a list item inside a list, false otherwise
 */
export const isDroppingListItemInsideList = (
  direction: Direction,
  $draggedPos: ResolvedPos,
  $dropTargetPos: ResolvedPos,
): boolean => {
  // if we are not dragging a list item, or we are dragging to the left or right, bail
  if (!isListItem($draggedPos.nodeAfter?.type) || [Direction.left, Direction.right].includes(direction)) {
    return false
  }

  if (direction === Direction.bottom && isListItem($dropTargetPos.nodeAfter?.type)) {
    return true
  }

  if (direction === Direction.top && isListItem($dropTargetPos.nodeAfter?.type)) {
    return true
  }

  return false
}

/**
 * Determines if the drop is just a change in depth (not a move operation)
 * @param direction the direction of the drop
 * @param $draggedPos the resolved position of the dragged node
 * @param $dropTargetPos the resolved position of the drop target
 * @param targetDepth the list-depth we want to drop the item at
 * @returns true if the drop is just a change in depth (not a move operation), false otherwise
 */
export const isListItemChangeDepthOperation = (
  direction: Direction,
  $draggedPos: ResolvedPos,
  $dropTargetPos: ResolvedPos,
  targetDepth: number,
) => {
  const isValidDirection = [Direction.top, Direction.bottom].includes(direction) // only consider top and bottom drops, all others are move operations
  const isSamePosition = $draggedPos === $dropTargetPos // the drop target is the same as the dragged item
  const _isDroppingListItemInsideList = isDroppingListItemInsideList(direction, $draggedPos, $dropTargetPos) // the only operation that can change depth is dropping a list item inside a list
  const doesResolveToSamePosition = (() => {
    const dropBelowResult =
      direction === Direction.bottom && // we are dropping below a list item
      $dropTargetPos.index() === $draggedPos.index() - 1 && // the target is the list item above the dragged item
      $dropTargetPos.pos + ($dropTargetPos.nodeAfter?.nodeSize ?? 0) === $draggedPos.pos // the resulting position is the same as the dragged item

    const dropAboveResult =
      direction === Direction.top && // we are dropping above a list item
      $dropTargetPos.index() === $draggedPos.index() + 1 && // the target is the list item below the dragged item
      $draggedPos.pos + ($draggedPos.nodeAfter?.nodeSize ?? 0) === $dropTargetPos.pos // the position of the target node is the same as the position after the dragged item

    return dropBelowResult || dropAboveResult
  })()

  const isDepthDifferent =
    _isDroppingListItemInsideList && // just to be safe, since we're about to assume the attrs are list_item or vote_list_item attrs
    (
      $draggedPos.nodeAfter?.attrs as ExtractStrongNodeSpecAttributes<
        typeof nodes.list_item | typeof nodes.vote_list_item
      >
    ).depth !== targetDepth // the target depth must be different from the current depth, otherwise this would be a no-op

  return isValidDirection && (isSamePosition || doesResolveToSamePosition) && isDepthDifferent
}

/**
 * Determines if you are attempting to drop a list_item below a list
 * @param direction the direction of the drop
 * @param $draggedPos the resolved position of the dragged node
 * @param $dropTargetPos the resolved position of the drop target
 * @returns true if the drop is below a list, false otherwise
 */
export const isDroppingListItemBelowList = (
  direction: Direction,
  $draggedPos: ResolvedPos,
  $dropTargetPos: ResolvedPos,
) => {
  if ([Direction.left, Direction.right].includes(direction)) return false // only consider vertical drops

  if (!isListItem($draggedPos.nodeAfter?.type)) return false // only consider drops of list items
  // below this point, we know we're dragging a list item

  if (direction === Direction.top) {
    // we're checking if we're dropping above the element immediately after the list
    // which is the same as dropping below the list
    const depthOfDropTarget = $dropTargetPos.depth // prosemirror depth (not indentation depth), will probably be 0
    const dropTargetIndex = $dropTargetPos.index()
    const draggedListIndex = $draggedPos.index(depthOfDropTarget)
    return dropTargetIndex === draggedListIndex + 1
  }

  return $dropTargetPos.nodeAfter?.type.groups.includes('list') ?? false // only consider drops that target a list
}

export const isDroppingNonListItemInsideList = ($draggedPos: ResolvedPos, $dropTargetPos: ResolvedPos) => {
  if (isListItem($draggedPos.nodeAfter?.type)) {
    // if we're dragging a list item, then we're by definition not dropping a non-list item inside
    // a list
    return false
  }
  if (!isListItem($dropTargetPos.nodeAfter?.type)) {
    // if the target of the drop is not a list item, then we're by definition not dropping a
    // non-list item inside a list
    return false
  }
  return true
}
